import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Onboarding from "@/pages/Onboarding";
import Quiz from "@/pages/Quiz";
import InvitationActivation from "@/pages/InvitationActivation";
import Formules from "@/pages/Formules";
import Landing from "@/pages/Landing";
import PagePublique from "@/pages/PagePublique";
import { MentionsLegales, PolitiqueConfidentialite, Cgu } from "@/pages/DocumentsLegaux";
import AdminAbonnements from "@/pages/AdminAbonnements";
import Dashboard from "@/pages/Dashboard";
import Placeholder from "@/pages/Placeholder";
import Clients from "@/pages/Clients";
import Fournisseurs from "@/pages/Fournisseurs";
import Lettrage from "@/pages/Lettrage";
import ImmobilisationsFichier from "@/pages/ImmobilisationsFichier";
import ImmobilisationsTableau from "@/pages/ImmobilisationsTableau";
import ImmobilisationsDotations from "@/pages/ImmobilisationsDotations";
import FactureClient from "@/pages/FactureClient";
import FactureFournisseur from "@/pages/FactureFournisseur";
import Banque from "@/pages/Banque";
import Caisse from "@/pages/Caisse";
import OperationsDiverses from "@/pages/OperationsDiverses";
import ODSalaires from "@/pages/ODSalaires";
import BalanceGenerale from "@/pages/BalanceGenerale";
import GrandLivre from "@/pages/GrandLivre";
import Journaux from "@/pages/Journaux";
import CompteResultat from "@/pages/CompteResultat";
import Bilan from "@/pages/Bilan";
import DeclarationTVA from "@/pages/DeclarationTVA";
import RapprochementBanque from "@/pages/RapprochementBanque";
import Parametres from "@/pages/Parametres";
import Aide from "@/pages/Aide";
import ComparatifFormules from "@/pages/ComparatifFormules";
import Contact from "@/pages/Contact";
import NotFound from "@/pages/NotFound";

// Définition des sections affichant un écran « à venir » pour l'instant.
const PLACEHOLDERS = [];

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Page d'accueil publique (vitrine) */}
            <Route path="/" element={<Landing />} />

            {/* Pages légales publiques */}
            <Route path="/mentions-legales" element={<MentionsLegales />} />
            <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
            <Route path="/politique-de-confidentialite" element={<PolitiqueConfidentialite />} />
            <Route path="/cgu" element={<Cgu />} />
            <Route path="/comparatif-formules" element={<ComparatifFormules />} />
            <Route path="/contact" element={<Contact />} />

            {/* Routes publiques d'authentification */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/invitation/:token" element={<InvitationActivation />} />

            {/* Onboarding : authentifié mais sans entreprise requise */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute exigerEntreprise={false}>
                  <Onboarding />
                </ProtectedRoute>
              }
            />

            {/* Questionnaire de positionnement : entreprise requise, mais sans
                exiger l'accès saisie (c'est cette page qui le débloque). */}
            <Route
              path="/quiz"
              element={
                <ProtectedRoute exigerAcces={false} exigerAbonnement={false}>
                  <Quiz />
                </ProtectedRoute>
              }
            />

            {/* Choix de la formule d'abonnement (après onboarding + quiz) */}
            <Route
              path="/formules"
              element={
                <ProtectedRoute exigerAbonnement={false}>
                  <Formules />
                </ProtectedRoute>
              }
            />

            {/* Espace administrateur AERH (authentifié, sans entreprise requise) */}
            <Route
              path="/admin/abonnements"
              element={
                <ProtectedRoute exigerEntreprise={false}>
                  <AdminAbonnements />
                </ProtectedRoute>
              }
            />

            {/* Espace principal protégé (entreprise requise) — layout sans préfixe */}
            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/tableau-de-bord" element={<Dashboard />} />
              <Route path="/journaux/ventes" element={<FactureClient />} />
              <Route path="/journaux/achats" element={<FactureFournisseur />} />
              <Route path="/journaux/banque" element={<Banque />} />
              <Route path="/journaux/caisse" element={<Caisse />} />
              <Route path="/journaux/od" element={<OperationsDiverses />} />
              <Route path="/journaux/od-salaires" element={<ODSalaires />} />
              <Route path="/etats/balance" element={<BalanceGenerale />} />
              <Route path="/etats/grand-livre" element={<GrandLivre />} />
              <Route path="/etats/journaux" element={<Journaux />} />
              <Route path="/etats/compte-de-resultat" element={<CompteResultat />} />
              <Route path="/etats/bilan" element={<Bilan />} />
              <Route path="/etats/declaration-tva" element={<DeclarationTVA />} />
              <Route path="/tiers/clients" element={<Clients />} />
              <Route path="/tiers/fournisseurs" element={<Fournisseurs />} />
              <Route path="/tiers/lettrage" element={<Lettrage />} />
              <Route path="/immobilisations" element={<Navigate to="/immobilisations/fichier" replace />} />
              <Route path="/immobilisations/fichier" element={<ImmobilisationsFichier />} />
              <Route path="/immobilisations/tableau" element={<ImmobilisationsTableau />} />
              <Route path="/immobilisations/dotations" element={<ImmobilisationsDotations />} />
              <Route path="/rapprochements/banque" element={<RapprochementBanque />} />
              <Route path="/parametres" element={<Parametres />} />
              <Route path="/aide" element={<Aide />} />
              {PLACEHOLDERS.map((p) => (
                <Route key={p.chemin} path={p.chemin} element={<Placeholder titre={p.titre} />} />
              ))}
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
